import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart3,
  Building2,
  CreditCard,
  Download,
  List,
  Maximize2,
  Search,
  Table2,
  TrendingUp,
  Users,
  Wallet,
  X
} from 'lucide-react';
import {
  Bar,
  ComposedChart,
  CartesianGrid,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import ResumoCard from './ResumoCardContratos';
import Modal from 'components/common/Modal';
import { formatarMoeda } from 'utils/formatters';
import { downloadXlsxFile } from 'utils/xlsxExport';

const PLAN_VIEW_STORAGE_KEY = 'contratos.planos.visualizacao';

const currencyCompact = (value) => formatarMoeda(Number(value) || 0);

const percentFormat = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1
});

const formatPercent = (value) => `${percentFormat.format(Number(value) || 0)}%`;

const formatCount = (value) => Number(value || 0).toLocaleString('pt-BR');

const parseNumericValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === 'string') {
    const sanitized = value.trim().replace(/\s/g, '');

    if (!sanitized) {
      return 0;
    }

    if (sanitized.includes(',') && sanitized.includes('.')) {
      return Number(sanitized.replace(/\./g, '').replace(',', '.')) || 0;
    }

    if (sanitized.includes(',')) {
      return Number(sanitized.replace(',', '.')) || 0;
    }

    return Number(sanitized) || 0;
  }

  return Number(value) || 0;
};

const readPlanValue = (plan, keys) => {
  for (const key of keys) {
    const value = plan?.[key];
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }

  return 0;
};

const normalizePlano = (plano = {}) => {
  const nome = readPlanValue(plano, ['nome', 'vd_contratos_nome', 'nome_plano', 'plano', 'descricao']);
  const idPlano = readPlanValue(plano, ['idPlano', 'id_plano', 'id']);

  return {
    ...plano,
    nome: nome || 'Plano não informado',
    idPlano,
    totalContratos: parseNumericValue(readPlanValue(plano, ['totalContratos', 'total_contratos', 'contratos_total'])),
    contratosPf: parseNumericValue(readPlanValue(plano, ['contratosPf', 'contratos_pf', 'contratosFisicos', 'contratos_fisicos'])),
    contratosPj: parseNumericValue(readPlanValue(plano, ['contratosPj', 'contratos_pj', 'contratosJuridicos', 'contratos_juridicos'])),
    faturamentoTotal: parseNumericValue(readPlanValue(plano, ['faturamentoTotal', 'faturamento_total', 'valorTotal', 'valor_total'])),
    faturamentoPf: parseNumericValue(readPlanValue(plano, ['faturamentoPf', 'faturamento_pf', 'faturamentoPessoaFisica', 'faturamento_pessoa_fisica', 'faturamento_pessoaFisica'])),
    faturamentoPj: parseNumericValue(readPlanValue(plano, ['faturamentoPj', 'faturamento_pj', 'faturamentoPessoaJuridica', 'faturamento_pessoa_juridica', 'faturamento_pessoaJuridica'])),
    ativos: parseNumericValue(readPlanValue(plano, ['ativos'])),
    suspensos: parseNumericValue(readPlanValue(plano, ['suspensos'])),
    contratosBloqueioManual: parseNumericValue(readPlanValue(plano, ['contratosBloqueioManual', 'contratos_bloqueio_manual'])),
    valorBloqueioManual: parseNumericValue(readPlanValue(plano, ['valorBloqueioManual', 'valor_bloqueio_manual'])),
    contratosEmitidos: parseNumericValue(readPlanValue(plano, ['contratosEmitidos', 'titulosEmitidos', 'titulos_emitidos'])),
    valorEmitido: parseNumericValue(readPlanValue(plano, ['valorEmitido', 'valor_emitido'])),
    valorAteVencimento: parseNumericValue(readPlanValue(plano, ['valorAteVencimento', 'valor_ate_vencimento'])),
    descontoTotal: parseNumericValue(readPlanValue(plano, ['descontoTotal', 'desconto_total'])),
    contratosRecebidos: parseNumericValue(readPlanValue(plano, ['contratosRecebidos', 'titulosRecebidos', 'titulos_recebidos'])),
    valorRecebido: parseNumericValue(readPlanValue(plano, ['valorRecebido', 'valor_recebido'])),
    contratosEmitidosPf: parseNumericValue(readPlanValue(plano, ['contratosEmitidosPf', 'titulosEmitidosPf', 'titulos_emitidos_pf'])),
    valorEmitidoPf: parseNumericValue(readPlanValue(plano, ['valorEmitidoPf', 'valor_emitido_pf'])),
    contratosRecebidosPf: parseNumericValue(readPlanValue(plano, ['contratosRecebidosPf', 'titulosRecebidosPf', 'titulos_recebidos_pf'])),
    valorRecebidoPf: parseNumericValue(readPlanValue(plano, ['valorRecebidoPf', 'valor_recebido_pf'])),
    contratosEmitidosPj: parseNumericValue(readPlanValue(plano, ['contratosEmitidosPj', 'titulosEmitidosPj', 'titulos_emitidos_pj'])),
    valorEmitidoPj: parseNumericValue(readPlanValue(plano, ['valorEmitidoPj', 'valor_emitido_pj'])),
    contratosRecebidosPj: parseNumericValue(readPlanValue(plano, ['contratosRecebidosPj', 'titulosRecebidosPj', 'titulos_recebidos_pj'])),
    valorRecebidoPj: parseNumericValue(readPlanValue(plano, ['valorRecebidoPj', 'valor_recebido_pj'])),
    titulosEmitidos: parseNumericValue(readPlanValue(plano, ['titulosEmitidos', 'titulos_emitidos', 'contratosEmitidos'])),
    titulosRecebidos: parseNumericValue(readPlanValue(plano, ['titulosRecebidos', 'titulos_recebidos', 'contratosRecebidos'])),
    titulosEmitidosPf: parseNumericValue(readPlanValue(plano, ['titulosEmitidosPf', 'titulos_emitidos_pf', 'contratosEmitidosPf'])),
    titulosRecebidosPf: parseNumericValue(readPlanValue(plano, ['titulosRecebidosPf', 'titulos_recebidos_pf', 'contratosRecebidosPf'])),
    titulosEmitidosPj: parseNumericValue(readPlanValue(plano, ['titulosEmitidosPj', 'titulos_emitidos_pj', 'contratosEmitidosPj'])),
    titulosRecebidosPj: parseNumericValue(readPlanValue(plano, ['titulosRecebidosPj', 'titulos_recebidos_pj', 'contratosRecebidosPj']))
  };
};

const normalizeCidade = (cidade = {}) => ({
  ...cidade,
  idCidade: readPlanValue(cidade, ['idCidade', 'id_cidade', 'id']),
  cidadeNome: readPlanValue(cidade, ['cidadeNome', 'cidade_nome', 'nome']) || 'Cidade não informada',
  totalContratos: parseNumericValue(readPlanValue(cidade, ['totalContratos', 'total_contratos'])),
  valorTotal: parseNumericValue(readPlanValue(cidade, ['valorTotal', 'valor_total'])),
  ativos: parseNumericValue(readPlanValue(cidade, ['ativos'])),
  suspensos: parseNumericValue(readPlanValue(cidade, ['suspensos'])),
  pessoasFisicas: parseNumericValue(readPlanValue(cidade, ['pessoasFisicas', 'pessoas_fisicas'])),
  pessoasJuridicas: parseNumericValue(readPlanValue(cidade, ['pessoasJuridicas', 'pessoas_juridicas'])),
  faturamentoPessoaFisica: parseNumericValue(readPlanValue(cidade, ['faturamentoPessoaFisica', 'faturamento_pessoa_fisica'])),
  faturamentoPessoaJuridica: parseNumericValue(readPlanValue(cidade, ['faturamentoPessoaJuridica', 'faturamento_pessoa_juridica'])),
  contratosBloqueioManual: parseNumericValue(readPlanValue(cidade, ['contratosBloqueioManual', 'contratos_bloqueio_manual'])),
  valorBloqueioManual: parseNumericValue(readPlanValue(cidade, ['valorBloqueioManual', 'valor_bloqueio_manual'])),
  titulosEmitidos: parseNumericValue(readPlanValue(cidade, ['titulosEmitidos', 'titulos_emitidos'])),
  contratosEmitidos: parseNumericValue(readPlanValue(cidade, ['contratosEmitidos', 'contratos_emitidos'])),
  valorEmitido: parseNumericValue(readPlanValue(cidade, ['valorEmitido', 'valor_emitido'])),
  valorAteVencimento: parseNumericValue(readPlanValue(cidade, ['valorAteVencimento', 'valor_ate_vencimento'])),
  descontoTotal: parseNumericValue(readPlanValue(cidade, ['descontoTotal', 'desconto_total'])),
  titulosRecebidos: parseNumericValue(readPlanValue(cidade, ['titulosRecebidos', 'titulos_recebidos'])),
  contratosRecebidos: parseNumericValue(readPlanValue(cidade, ['contratosRecebidos', 'contratos_recebidos'])),
  valorRecebido: parseNumericValue(readPlanValue(cidade, ['valorRecebido', 'valor_recebido'])),
  titulosEmitidosPf: parseNumericValue(readPlanValue(cidade, ['titulosEmitidosPf', 'titulos_emitidos_pf'])),
  contratosEmitidosPf: parseNumericValue(readPlanValue(cidade, ['contratosEmitidosPf', 'contratos_emitidos_pf'])),
  valorEmitidoPf: parseNumericValue(readPlanValue(cidade, ['valorEmitidoPf', 'valor_emitido_pf'])),
  titulosRecebidosPf: parseNumericValue(readPlanValue(cidade, ['titulosRecebidosPf', 'titulos_recebidos_pf'])),
  contratosRecebidosPf: parseNumericValue(readPlanValue(cidade, ['contratosRecebidosPf', 'contratos_recebidos_pf'])),
  valorRecebidoPf: parseNumericValue(readPlanValue(cidade, ['valorRecebidoPf', 'valor_recebido_pf'])),
  titulosEmitidosPj: parseNumericValue(readPlanValue(cidade, ['titulosEmitidosPj', 'titulos_emitidos_pj'])),
  contratosEmitidosPj: parseNumericValue(readPlanValue(cidade, ['contratosEmitidosPj', 'contratos_emitidos_pj'])),
  valorEmitidoPj: parseNumericValue(readPlanValue(cidade, ['valorEmitidoPj', 'valor_emitido_pj'])),
  titulosRecebidosPj: parseNumericValue(readPlanValue(cidade, ['titulosRecebidosPj', 'titulos_recebidos_pj'])),
  contratosRecebidosPj: parseNumericValue(readPlanValue(cidade, ['contratosRecebidosPj', 'contratos_recebidos_pj'])),
  valorRecebidoPj: parseNumericValue(readPlanValue(cidade, ['valorRecebidoPj', 'valor_recebido_pj'])),
  planos: Array.isArray(cidade.planos) ? cidade.planos.map(normalizePlano) : []
});

const getCurrentTheme = () => {
  if (typeof window === 'undefined') {
    return 'light';
  }

  try {
    return localStorage.getItem('theme_preference') === 'dark' ? 'dark' : 'light';
  } catch (error) {
    return 'light';
  }
};

const getInitialPlanViewMode = () => {
  if (typeof window === 'undefined') {
    return 'tabela';
  }

  try {
    const stored = localStorage.getItem(PLAN_VIEW_STORAGE_KEY);
    return stored === 'lista' ? 'lista' : 'tabela';
  } catch (error) {
    return 'tabela';
  }
};

const StatusPill = ({ label, value, tone = 'cyan', sublabel = null, currentTheme = 'light' }) => {
  const tones = {
    cyan: currentTheme === 'dark'
      ? 'border-sky-500/20 bg-sky-500/10 text-sky-300'
      : 'border-sky-200 bg-sky-50 text-sky-950 shadow-[0_1px_2px_rgba(15,23,42,0.04)]',
    emerald: currentTheme === 'dark'
      ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-300'
      : 'border-emerald-200 bg-emerald-50 text-emerald-950 shadow-[0_1px_2px_rgba(15,23,42,0.04)]',
    amber: currentTheme === 'dark'
      ? 'border-amber-500/20 bg-amber-500/10 text-amber-300'
      : 'border-amber-200 bg-amber-50 text-amber-950 shadow-[0_1px_2px_rgba(15,23,42,0.04)]',
    violet: currentTheme === 'dark'
      ? 'border-violet-500/20 bg-violet-500/10 text-violet-300'
      : 'border-violet-200 bg-violet-50 text-violet-950 shadow-[0_1px_2px_rgba(15,23,42,0.04)]'
  };

  return (
    <div className={`rounded-2xl border px-3 py-2 ${tones[tone] || tones.cyan}`}>
      <p className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${currentTheme === 'dark' ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}`}>
        {label}
      </p>
      <p className={`mt-1 text-sm font-semibold ${currentTheme === 'dark' ? 'text-[color:var(--text-primary)]' : 'text-slate-950'}`}>
        {value}
      </p>
      {sublabel && (
        <p className={`mt-0.5 text-xs ${currentTheme === 'dark' ? 'text-[color:var(--text-muted)]' : 'text-slate-600'}`}>
          {sublabel}
        </p>
      )}
    </div>
  );
};

const MetricCard = ({ label, value, detail, tone = 'cyan', currentTheme = 'light' }) => {
  const tones = {
    cyan: '#38bdf8',
    emerald: '#34d399',
    amber: '#fbbf24',
    violet: '#a78bfa',
    rose: '#fb7185'
  };

  const toneColor = tones[tone] || tones.cyan;
  const isDark = currentTheme === 'dark';
  const cardToneClasses = isDark
    ? 'border-slate-800/70 bg-[color:var(--surface)]/92 shadow-[0_10px_28px_rgba(2,6,23,0.14)]'
    : 'border-slate-200/80 bg-white shadow-[0_12px_24px_rgba(15,23,42,0.08)]';

  return (
    <div className={`group relative overflow-hidden rounded-[18px] border px-4 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_30px_rgba(2,6,23,0.14)] ${cardToneClasses}`}>
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[color:var(--accent)]/18 to-transparent" />
      <div
        className="pointer-events-none absolute inset-y-0 left-0 w-1.5 rounded-r-full opacity-90"
        style={{ backgroundColor: toneColor }}
      />
      <div className="relative pl-2">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className={`text-[10px] font-semibold uppercase tracking-[0.22em] ${isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}`}>
              {label}
            </p>
            <p className={`mt-2 text-[1.5rem] font-semibold leading-none tracking-tight tabular-nums sm:text-[1.7rem] ${isDark ? 'text-[color:var(--text-primary)]' : 'text-slate-950'}`}>
              {value}
            </p>
          </div>
        </div>

        {detail && (
          <div className="mt-3 border-t border-dashed border-current/8 pt-3">
            <p className={`text-xs leading-relaxed ${isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-600'}`}>
              {detail}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const PlanCard = ({ plano, index, totalFaturamento, currentTheme = 'light' }) => {
  const totalContratos = Number(plano.totalContratos || 0);
  const contratosPf = Number(plano.contratosPf || 0);
  const contratosPj = Number(plano.contratosPj || 0);
  const faturamentoPf = Number(plano.faturamentoPf || 0);
  const faturamentoPj = Number(plano.faturamentoPj || 0);
  const faturamentoTotal = Number(plano.faturamentoTotal || 0);
  const ativos = Number(plano.ativos || 0);
  const suspensos = Number(plano.suspensos || 0);
  const contratosBloqueioManual = Number(plano.contratosBloqueioManual || 0);
  const valorBloqueioManual = Number(plano.valorBloqueioManual || 0);
  const titulosEmitidos = Number(plano.titulosEmitidos ?? 0);
  const valorEmitido = Number(plano.valorEmitido || 0);
  const valorAteVencimento = Number(plano.valorAteVencimento || 0);
  const descontoTotal = Number(plano.descontoTotal || 0);
  const titulosRecebidos = Number(plano.titulosRecebidos ?? 0);
  const valorRecebido = Number(plano.valorRecebido || 0);
  const contratosEmitidosPf = Number(plano.contratosEmitidosPf ?? plano.titulosEmitidosPf ?? 0);
  const valorEmitidoPf = Number(plano.valorEmitidoPf || 0);
  const contratosRecebidosPf = Number(plano.contratosRecebidosPf ?? plano.titulosRecebidosPf ?? 0);
  const valorRecebidoPf = Number(plano.valorRecebidoPf || 0);
  const contratosEmitidosPj = Number(plano.contratosEmitidosPj ?? plano.titulosEmitidosPj ?? 0);
  const valorEmitidoPj = Number(plano.valorEmitidoPj || 0);
  const contratosRecebidosPj = Number(plano.contratosRecebidosPj ?? plano.titulosRecebidosPj ?? 0);
  const valorRecebidoPj = Number(plano.valorRecebidoPj || 0);
  const baseTotal = totalContratos || contratosPf + contratosPj;
  const shareReceita = totalFaturamento > 0 ? (faturamentoTotal / totalFaturamento) * 100 : 0;
  const basePf = baseTotal > 0 ? (contratosPf / baseTotal) * 100 : 0;
  const basePj = baseTotal > 0 ? (contratosPj / baseTotal) * 100 : 0;
  const isDark = currentTheme === 'dark';

  return (
    <article
      className={`rounded-[24px] border p-4 sm:p-5 ${
        isDark
          ? 'border-[color:var(--border)] bg-[linear-gradient(180deg,rgba(255,255,255,0.03),rgba(255,255,255,0.015))] shadow-[0_12px_40px_rgba(2,6,23,0.12)]'
          : 'border-[color:var(--border)] bg-white shadow-[0_14px_36px_rgba(15,23,42,0.08)]'
      }`}
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border text-sm font-semibold ${
              isDark
                ? 'border-[color:var(--border)] bg-[color:var(--surface-muted)]/80 text-[color:var(--text-primary)]'
                : 'border-[color:var(--border)] bg-[color:var(--surface-muted)] text-slate-900'
            }`}>
              {index + 1}
            </div>
            <div className="min-w-0">
              <p className={`text-[10px] font-semibold uppercase tracking-[0.2em] ${isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}`}>
                Plano
              </p>
              <h3 className="truncate text-base font-semibold text-[color:var(--text-primary)] sm:text-lg">
                {plano.nome || 'Plano não informado'}
              </h3>
            </div>
          </div>

          <p className={`mt-2 text-sm ${isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}`}>
            {plano.idPlano ? `ID ${plano.idPlano}` : 'Sem identificador'} • {formatCount(baseTotal)} contratos
          </p>
        </div>

        <div className={`rounded-2xl border px-4 py-3 lg:min-w-[180px] lg:text-right ${
          isDark
            ? 'border-[color:var(--border)] bg-[color:var(--surface-muted)]/60'
            : 'border-[color:var(--border)] bg-slate-50'
        }`}>
          <p className={`text-[10px] font-semibold uppercase tracking-[0.2em] ${isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}`}>
            Receita total
          </p>
          <p className="mt-1 text-xl font-semibold tracking-tight text-[color:var(--text-primary)]">
            {currencyCompact(faturamentoTotal)}
          </p>
          <p className={`text-xs ${isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}`}>
            {formatPercent(shareReceita)} da receita
          </p>
        </div>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[1.15fr_0.85fr]">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className={`rounded-[20px] border p-4 ${
            isDark
              ? 'border-[color:var(--border)] bg-[color:var(--surface)]/70'
              : 'border-sky-200 bg-sky-50'
          }`}>
            <div className={`flex items-center justify-between gap-3 text-xs ${isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}`}>
              <span>PF</span>
              <span className="tabular-nums">{formatPercent(basePf)}</span>
            </div>
            <div className="mt-3 space-y-2">
              <div>
                <p className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-sky-300/80' : 'text-sky-700'}`}>
                  Emitido
                </p>
                <p className={`mt-1 text-sm font-semibold ${isDark ? 'text-sky-300' : 'text-sky-700'}`}>
                  {formatCount(contratosEmitidosPf)} • {currencyCompact(valorEmitidoPf)}
                </p>
              </div>
              <div>
                <p className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-sky-300/80' : 'text-sky-700'}`}>
                  Faturado esperado
                </p>
                <p className={`mt-1 text-sm font-semibold ${isDark ? 'text-sky-300' : 'text-sky-700'}`}>
                  {currencyCompact(faturamentoPf)}
                </p>
              </div>
              <div>
                <p className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-sky-300/80' : 'text-sky-700'}`}>
                  Recebido
                </p>
                <p className={`mt-1 text-sm font-semibold ${isDark ? 'text-sky-300' : 'text-sky-700'}`}>
                  {formatCount(contratosRecebidosPf)} • {currencyCompact(valorRecebidoPf)}
                </p>
              </div>
            </div>
          </div>

          <div className={`rounded-[20px] border p-4 ${
            isDark
              ? 'border-[color:var(--border)] bg-[color:var(--surface)]/70'
              : 'border-violet-200 bg-violet-50'
          }`}>
            <div className={`flex items-center justify-between gap-3 text-xs ${isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}`}>
              <span>PJ</span>
              <span className="tabular-nums">{formatPercent(basePj)}</span>
            </div>
            <div className="mt-3 space-y-2">
              <div>
                <p className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-violet-300/80' : 'text-violet-700'}`}>
                  Emitido
                </p>
                <p className={`mt-1 text-sm font-semibold ${isDark ? 'text-violet-300' : 'text-violet-700'}`}>
                  {formatCount(contratosEmitidosPj)} • {currencyCompact(valorEmitidoPj)}
                </p>
              </div>
              <div>
                <p className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-violet-300/80' : 'text-violet-700'}`}>
                  Faturado esperado
                </p>
                <p className={`mt-1 text-sm font-semibold ${isDark ? 'text-violet-300' : 'text-violet-700'}`}>
                  {currencyCompact(faturamentoPj)}
                </p>
              </div>
              <div>
                <p className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-violet-300/80' : 'text-violet-700'}`}>
                  Recebido
                </p>
                <p className={`mt-1 text-sm font-semibold ${isDark ? 'text-violet-300' : 'text-violet-700'}`}>
                  {formatCount(contratosRecebidosPj)} • {currencyCompact(valorRecebidoPj)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <StatusPill
            label="Ativos"
            value={formatCount(ativos)}
            tone="emerald"
            sublabel="Contratos em operação"
            currentTheme={currentTheme}
          />
          <StatusPill
            label="Suspensos"
            value={formatCount(suspensos)}
            tone="amber"
            sublabel="Contratos fora da operação"
            currentTheme={currentTheme}
          />
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Bloqueio manual"
          value={formatCount(contratosBloqueioManual)}
          detail={currencyCompact(valorBloqueioManual)}
          tone="rose"
          currentTheme={currentTheme}
        />
        <MetricCard
          label="Títulos emitidos"
          value={formatCount(titulosEmitidos)}
          detail={currencyCompact(valorEmitido)}
          tone="cyan"
          currentTheme={currentTheme}
        />
        <MetricCard
          label="Até vencimento"
          value={currencyCompact(valorAteVencimento)}
          detail={`Desconto ${currencyCompact(descontoTotal)}`}
          tone="amber"
          currentTheme={currentTheme}
        />
        <MetricCard
          label="Títulos recebidos"
          value={formatCount(titulosRecebidos)}
          detail={currencyCompact(valorRecebido)}
          tone="emerald"
          currentTheme={currentTheme}
        />
      </div>
    </article>
  );
};

const PlanosTableEmptyState = ({ currentTheme, title, description }) => {
  const isDark = currentTheme === 'dark';

  return (
    <div className={`rounded-[24px] border p-8 text-center ${
      isDark
        ? 'border-[color:var(--border)] bg-[color:var(--surface-muted)]/55'
        : 'border-slate-200 bg-white shadow-[0_12px_28px_rgba(15,23,42,0.05)]'
    }`}>
      <Table2 className={`mx-auto h-10 w-10 ${isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-400'}`} />
      <p className={`mt-4 text-base font-semibold ${isDark ? 'text-[color:var(--text-primary)]' : 'text-slate-900'}`}>
        {title}
      </p>
      <p className={`mt-2 text-sm ${isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}`}>
        {description}
      </p>
    </div>
  );
};

const PlanosCompactTable = ({ planos, totalFaturamento, currentTheme = 'light', onExpand, searchValue, onSearchChange, onClearSearch }) => {
  const isDark = currentTheme === 'dark';
  const compactColumns = [
    { titulo: 'Plano', largura: '28%' },
    { titulo: 'Contratos', largura: '12%' },
    { titulo: 'Receita total', largura: '18%' },
    { titulo: 'Recebido PF', largura: '21%' },
    { titulo: 'Recebido PJ', largura: '21%' }
  ];

  if (!planos.length) {
    return (
      <PlanosTableEmptyState
        currentTheme={currentTheme}
        title="Nenhum plano disponível"
        description="Quando o n8n enviar o array `planos`, ele aparecerá aqui com a visão consolidada."
      />
    );
  }

  return (
    <div className={`flex h-[760px] min-h-[760px] flex-col overflow-hidden rounded-[24px] border ${
      isDark
        ? 'border-[color:var(--border)] bg-[color:var(--surface)]/88'
        : 'border-slate-200 bg-white shadow-[0_12px_28px_rgba(15,23,42,0.05)]'
    }`}>
      <div className={`flex items-start justify-between gap-3 border-b px-4 py-4 ${
        isDark ? 'border-[color:var(--border)]' : 'border-slate-200'
      }`}>
        <div className="min-w-0">
          <p className={`text-[10px] font-semibold uppercase tracking-[0.2em] ${isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}`}>
            Tabela compacta
          </p>
          <p className={`mt-1 text-sm ${isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}`}>
            Leitura rápida dos planos com PF/PJ, receita e recebimento por tipo.
          </p>
        </div>

        <button
          type="button"
          onClick={onExpand}
          className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold transition-colors ${
            isDark
              ? 'border-[color:var(--border)] bg-[color:var(--surface-muted)]/55 text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)]/75'
              : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'
          }`}
          aria-label="Abrir visão expandida dos planos"
        >
          <Maximize2 className="h-4 w-4" />
          Expandir
        </button>
      </div>

      <div className={`flex items-center justify-between gap-3 border-b px-4 py-3 text-xs ${
        isDark ? 'border-[color:var(--border)]' : 'border-slate-200'
      }`}>
        <p className={isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}>
          {formatCount(planos.length)} planos filtrados
        </p>
        <p className={isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}>
          {currencyCompact(totalFaturamento)} total
        </p>
      </div>

      <div className={`flex flex-col gap-3 border-b px-4 py-4 sm:flex-row sm:items-center sm:justify-between ${
        isDark ? 'border-[color:var(--border)]' : 'border-slate-200'
      }`}>
        <div className="min-w-0">
          <p className={`text-[10px] font-semibold uppercase tracking-[0.2em] ${isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}`}>
            Pesquisar plano
          </p>
          <p className={`mt-1 text-sm ${isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}`}>
            Filtre por nome ou ID dentro da tabela.
          </p>
        </div>

        <div className="relative w-full sm:max-w-md">
          <Search className={`pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 ${isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-400'}`} />
          <input
            type="search"
            value={searchValue}
            onChange={(event) => onSearchChange?.(event.target.value)}
            placeholder="Buscar plano..."
            aria-label="Buscar plano"
            className={`h-11 w-full rounded-2xl border pl-10 pr-10 text-sm outline-none transition-colors placeholder:text-[color:var(--text-muted)] focus:border-[color:var(--accent)] focus:ring-1 focus:ring-[color:var(--accent)]/30 ${
              isDark
                ? 'border-[color:var(--border)] bg-[color:var(--surface)]/90 text-[color:var(--text-primary)]'
                : 'border-slate-200 bg-[linear-gradient(180deg,rgba(255,255,255,0.99),rgba(248,250,252,0.98))] text-slate-900 shadow-[0_1px_2px_rgba(15,23,42,0.04)]'
            }`}
          />
          {searchValue && (
            <button
              type="button"
              onClick={() => onClearSearch?.()}
              className={`absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-xl transition-colors ${
                isDark
                  ? 'text-[color:var(--text-muted)] hover:bg-[color:var(--surface-muted)] hover:text-[color:var(--text-primary)]'
                  : 'text-slate-400 hover:bg-slate-100 hover:text-slate-900'
              }`}
              aria-label="Limpar busca"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden [scrollbar-gutter:stable]">
        <table className="w-full border-collapse table-fixed">
          <thead className="sticky top-0 z-20">
            <tr>
              {compactColumns.map((coluna) => (
                <th
                  key={coluna.titulo}
                  className={`sticky top-0 border-b px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-[0.18em] ${
                    isDark
                      ? 'border-[color:var(--border)] bg-[color:var(--surface)]/72 text-[color:var(--text-muted)] backdrop-blur-xl backdrop-saturate-150'
                      : 'border-slate-200 bg-white/78 text-slate-500 backdrop-blur-xl backdrop-saturate-150'
                  }`}
                  style={{ width: coluna.largura }}
                >
                  {coluna.titulo}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {planos.map((plano, index) => {
              const shareReceita = totalFaturamento > 0 ? (plano.faturamentoTotal / totalFaturamento) * 100 : 0;

              return (
                <tr
                  key={`${plano.idPlano ?? 'plano'}-${index}`}
                  className={`transition-colors ${
                    isDark
                      ? 'border-b border-[color:var(--border)] odd:bg-[color:var(--surface)] even:bg-[color:var(--surface-muted)]/15 hover:bg-[color:var(--surface-muted)]/35'
                      : 'border-b border-slate-200 odd:bg-white even:bg-slate-50/60 hover:bg-slate-100/70'
                  }`}
                >
                  <td className="px-4 py-4 align-top">
                    <div className="min-w-0">
                      <div className="flex items-start gap-3">
                        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border text-xs font-semibold ${
                          isDark
                            ? 'border-[color:var(--border)] bg-[color:var(--surface-muted)]/80 text-[color:var(--text-primary)]'
                            : 'border-slate-200 bg-slate-50 text-slate-900'
                        }`}>
                          {index + 1}
                        </div>
                        <div className="min-w-0">
                          <p className={`truncate text-sm font-semibold ${isDark ? 'text-[color:var(--text-primary)]' : 'text-slate-900'}`}>
                            {plano.nome}
                          </p>
                          <p className={`mt-1 text-xs ${isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}`}>
                            ID {plano.idPlano ?? '—'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </td>

                  <td className="px-4 py-4 align-top">
                    <div className="space-y-1 text-sm tabular-nums">
                      <p className={`font-semibold ${isDark ? 'text-[color:var(--text-primary)]' : 'text-slate-900'}`}>
                        {formatCount(plano.totalContratos)}
                      </p>
                      <p className={`text-xs ${isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}`}>
                        PF {formatCount(plano.contratosPf)} • PJ {formatCount(plano.contratosPj)}
                      </p>
                    </div>
                  </td>

                  <td className="px-4 py-4 align-top">
                    <div className="space-y-1 tabular-nums">
                      <p className={`text-sm font-semibold ${isDark ? 'text-[color:var(--text-primary)]' : 'text-slate-900'}`}>
                        {currencyCompact(plano.faturamentoTotal)}
                      </p>
                      <p className={`text-xs ${isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}`}>
                        {formatPercent(shareReceita)} da receita
                      </p>
                    </div>
                  </td>

                  <td className="px-4 py-4 align-top">
                    <p className={`${isDark ? 'text-[color:var(--text-primary)]' : 'text-slate-900'} text-sm font-semibold tabular-nums`}>
                      {formatCount(plano.titulosRecebidosPf)} • {currencyCompact(plano.valorRecebidoPf)}
                    </p>
                  </td>

                  <td className="px-4 py-4 align-top">
                    <p className={`${isDark ? 'text-[color:var(--text-primary)]' : 'text-slate-900'} text-sm font-semibold tabular-nums`}>
                      {formatCount(plano.titulosRecebidosPj)} • {currencyCompact(plano.valorRecebidoPj)}
                    </p>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PlanosExpandedTable = ({ planos, totalFaturamento, currentTheme = 'light' }) => {
  const isDark = currentTheme === 'dark';
  const expandedColumns = [
    { titulo: 'Plano', largura: '18%' },
    { titulo: 'Contratos', largura: '10%' },
    { titulo: 'Receita total', largura: '12%' },
    { titulo: 'Operação', largura: '12%' },
    { titulo: 'PF Emitido', largura: '9%' },
    { titulo: 'PF Faturado', largura: '9%' },
    { titulo: 'PF Recebido', largura: '9%' },
    { titulo: 'PJ Emitido', largura: '9%' },
    { titulo: 'PJ Faturado', largura: '9%' },
    { titulo: 'PJ Recebido', largura: '9%' }
  ];

  if (!planos.length) {
    return (
      <PlanosTableEmptyState
        currentTheme={currentTheme}
        title="Nenhum plano disponível na visão expandida"
        description="A visão detalhada será preenchida assim que a API retornar o array `planos`."
      />
    );
  }

  return (
    <div className={`flex h-[760px] min-h-[760px] flex-col overflow-hidden rounded-[24px] border ${
      isDark
        ? 'border-[color:var(--border)] bg-[color:var(--surface)]/88'
        : 'border-slate-200 bg-white shadow-[0_12px_28px_rgba(15,23,42,0.05)]'
    }`}>
      <div className={`flex items-center justify-between gap-3 border-b px-4 py-4 ${
        isDark ? 'border-[color:var(--border)]' : 'border-slate-200'
      }`}>
        <div>
          <p className={`text-[10px] font-semibold uppercase tracking-[0.2em] ${isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}`}>
            Visão expandida
          </p>
          <p className={`mt-1 text-sm ${isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}`}>
            Mais campos financeiros, sem perder a leitura por plano.
          </p>
        </div>
        <p className={`text-xs ${isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}`}>
          {formatCount(planos.length)} planos • {currencyCompact(totalFaturamento)} total
        </p>
      </div>

      <div className="min-h-0 flex-1 overflow-auto [scrollbar-gutter:stable]">
        <table className="w-full min-w-[1380px] border-collapse table-fixed">
          <thead className="sticky top-0 z-20">
            <tr>
              {expandedColumns.map((coluna) => (
                <th
                  key={coluna.titulo}
                  className={`sticky top-0 border-b px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-[0.18em] ${
                    isDark
                      ? 'border-[color:var(--border)] bg-[color:var(--surface)]/72 text-[color:var(--text-muted)] backdrop-blur-xl backdrop-saturate-150'
                      : 'border-slate-200 bg-white/78 text-slate-500 backdrop-blur-xl backdrop-saturate-150'
                  }`}
                  style={{ width: coluna.largura }}
                >
                  {coluna.titulo}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {planos.map((plano, index) => {
              const shareReceita = totalFaturamento > 0 ? (plano.faturamentoTotal / totalFaturamento) * 100 : 0;

              return (
                <tr
                  key={`${plano.idPlano ?? 'plano'}-${index}`}
                  className={`transition-colors ${
                    isDark
                      ? 'border-b border-[color:var(--border)] odd:bg-[color:var(--surface)] even:bg-[color:var(--surface-muted)]/15 hover:bg-[color:var(--surface-muted)]/35'
                      : 'border-b border-slate-200 odd:bg-white even:bg-slate-50/60 hover:bg-slate-100/70'
                  }`}
                >
                  <td className="px-4 py-4 align-top">
                    <div className="flex items-start gap-3">
                      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border text-sm font-semibold ${
                        isDark
                          ? 'border-[color:var(--border)] bg-[color:var(--surface-muted)]/80 text-[color:var(--text-primary)]'
                          : 'border-slate-200 bg-slate-50 text-slate-900'
                      }`}>
                        {index + 1}
                      </div>
                      <div className="min-w-0">
                        <p className={`truncate text-sm font-semibold ${isDark ? 'text-[color:var(--text-primary)]' : 'text-slate-900'}`}>
                          {plano.nome}
                        </p>
                        <p className={`mt-1 text-xs ${isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}`}>
                          ID {plano.idPlano ?? '—'}
                        </p>
                      </div>
                    </div>
                  </td>

                  <td className="px-4 py-4 align-top tabular-nums">
                    <p className={`font-semibold ${isDark ? 'text-[color:var(--text-primary)]' : 'text-slate-900'}`}>
                      {formatCount(plano.totalContratos)}
                    </p>
                    <p className={`mt-1 text-xs ${isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}`}>
                      PF {formatCount(plano.contratosPf)} • PJ {formatCount(plano.contratosPj)}
                    </p>
                  </td>

                  <td className="px-4 py-4 align-top tabular-nums">
                    <p className={`font-semibold ${isDark ? 'text-[color:var(--text-primary)]' : 'text-slate-900'}`}>
                      {currencyCompact(plano.faturamentoTotal)}
                    </p>
                    <p className={`mt-1 text-xs ${isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}`}>
                      {formatPercent(shareReceita)} da receita
                    </p>
                  </td>

                  <td className="px-4 py-4 align-top text-xs tabular-nums">
                    <p className={`font-semibold ${isDark ? 'text-[color:var(--text-primary)]' : 'text-slate-900'}`}>
                      Ativos {formatCount(plano.ativos)}
                    </p>
                    <p className={`${isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}`}>
                      Suspensos {formatCount(plano.suspensos)}
                    </p>
                  </td>

                  <td className="px-4 py-4 align-top text-xs tabular-nums">
                    <p className={`font-semibold ${isDark ? 'text-sky-300' : 'text-sky-700'}`}>
                      {formatCount(plano.titulosEmitidosPf)} • {currencyCompact(plano.valorEmitidoPf)}
                    </p>
                  </td>

                  <td className="px-4 py-4 align-top text-xs tabular-nums">
                    <p className={`font-semibold ${isDark ? 'text-sky-300' : 'text-sky-700'}`}>
                      {currencyCompact(plano.faturamentoPf)}
                    </p>
                  </td>

                  <td className="px-4 py-4 align-top text-xs tabular-nums">
                    <p className={`font-semibold ${isDark ? 'text-sky-300' : 'text-sky-700'}`}>
                      {formatCount(plano.titulosRecebidosPf)} • {currencyCompact(plano.valorRecebidoPf)}
                    </p>
                  </td>

                  <td className="px-4 py-4 align-top text-xs tabular-nums">
                    <p className={`font-semibold ${isDark ? 'text-violet-300' : 'text-violet-700'}`}>
                      {formatCount(plano.titulosEmitidosPj)} • {currencyCompact(plano.valorEmitidoPj)}
                    </p>
                  </td>

                  <td className="px-4 py-4 align-top text-xs tabular-nums">
                    <p className={`font-semibold ${isDark ? 'text-violet-300' : 'text-violet-700'}`}>
                      {currencyCompact(plano.faturamentoPj)}
                    </p>
                  </td>

                  <td className="px-4 py-4 align-top text-xs tabular-nums">
                    <p className={`font-semibold ${isDark ? 'text-violet-300' : 'text-violet-700'}`}>
                      {formatCount(plano.titulosRecebidosPj)} • {currencyCompact(plano.valorRecebidoPj)}
                    </p>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PlanosRevenueChart = ({ planos, totalFaturamento, currentTheme = 'light' }) => {
  const isDark = currentTheme === 'dark';

  const chartData = useMemo(
    () => {
      const orderedPlanos = [...planos]
        .sort((left, right) => (right.faturamentoTotal || 0) - (left.faturamentoTotal || 0))
        .slice(0, 5);

      const outrosPlanos = [...planos]
        .sort((left, right) => (right.faturamentoTotal || 0) - (left.faturamentoTotal || 0))
        .slice(5);

      let acumulado = 0;

      const normalizedPlanos = orderedPlanos.map((plano) => {
        const faturamentoTotalPlano = plano.faturamentoTotal || 0;
        const shareReceita = totalFaturamento > 0 ? (faturamentoTotalPlano / totalFaturamento) * 100 : 0;
        acumulado += shareReceita;

        return {
          nomeCurto: String(plano.nome || 'Plano').slice(0, 18),
          nomeCompleto: plano.nome || 'Plano não informado',
          faturamentoTotal: faturamentoTotalPlano,
          faturamentoPf: plano.faturamentoPf || 0,
          faturamentoPj: plano.faturamentoPj || 0,
          shareReceita,
          acumuladoReceita: Math.min(acumulado, 100),
          contratosPf: plano.contratosPf || 0,
          contratosPj: plano.contratosPj || 0,
          totalContratos: plano.totalContratos || 0
        };
      });

      if (!outrosPlanos.length) {
        return normalizedPlanos;
      }

      const outrosAggregate = outrosPlanos.reduce(
        (accumulator, plano) => {
          accumulator.faturamentoTotal += plano.faturamentoTotal || 0;
          accumulator.faturamentoPf += plano.faturamentoPf || 0;
          accumulator.faturamentoPj += plano.faturamentoPj || 0;
          accumulator.contratosPf += plano.contratosPf || 0;
          accumulator.contratosPj += plano.contratosPj || 0;
          accumulator.totalContratos += plano.totalContratos || 0;
          return accumulator;
        },
        {
          nomeCurto: 'Outros',
          nomeCompleto: `Outros (${outrosPlanos.length} planos)`,
          faturamentoTotal: 0,
          faturamentoPf: 0,
          faturamentoPj: 0,
          shareReceita: 0,
          acumuladoReceita: 0,
          contratosPf: 0,
          contratosPj: 0,
          totalContratos: 0
        }
      );

      outrosAggregate.shareReceita = totalFaturamento > 0 ? (outrosAggregate.faturamentoTotal / totalFaturamento) * 100 : 0;
      outrosAggregate.acumuladoReceita = Math.min(acumulado + outrosAggregate.shareReceita, 100);

      return [...normalizedPlanos, outrosAggregate];
    },
    [planos, totalFaturamento]
  );

  const tooltipContent = ({ active, payload }) => {
    if (!active || !payload?.length) {
      return null;
    }

    const item = payload[0]?.payload || {};

    return (
      <div className={`min-w-[240px] rounded-2xl border p-3 shadow-[0_14px_28px_rgba(2,6,23,0.12)] ${
        isDark
          ? 'border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text-primary)]'
          : 'border-slate-200 bg-white text-slate-900'
      }`}>
        <p className="text-sm font-semibold">{item.nomeCompleto}</p>
        <p className={`mt-1 text-xs ${isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}`}>
          {formatCount(item.totalContratos)} contratos • {formatPercent(item.shareReceita)} da receita
        </p>
        <div className="mt-3 space-y-2 text-xs">
          <div className="flex items-center justify-between gap-4">
            <span className={isDark ? 'text-sky-300' : 'text-sky-700'}>PF</span>
            <span className="tabular-nums font-semibold">{currencyCompact(item.faturamentoPf)}</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className={isDark ? 'text-violet-300' : 'text-violet-700'}>PJ</span>
            <span className="tabular-nums font-semibold">{currencyCompact(item.faturamentoPj)}</span>
          </div>
          <div className={`border-t pt-2 ${isDark ? 'border-[color:var(--border)]' : 'border-slate-200'}`}>
            <div className="flex items-center justify-between gap-4">
              <span className={isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}>Total</span>
              <span className="tabular-nums font-semibold">{currencyCompact(item.faturamentoTotal)}</span>
            </div>
          </div>
          <div className={`border-t pt-2 ${isDark ? 'border-[color:var(--border)]' : 'border-slate-200'}`}>
            <div className="flex items-center justify-between gap-4">
              <span className={isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}>Acumulado</span>
              <span className="tabular-nums font-semibold">{formatPercent(item.acumuladoReceita)}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`flex h-[760px] min-h-[760px] flex-col overflow-hidden rounded-[24px] border ${
      isDark
        ? 'border-[color:var(--border)] bg-[color:var(--surface)]/88'
        : 'border-slate-200 bg-white shadow-[0_12px_28px_rgba(15,23,42,0.05)]'
    }`}>
      <div className={`border-b px-4 py-4 ${isDark ? 'border-[color:var(--border)]' : 'border-slate-200'}`}>
        <p className={`text-[10px] font-semibold uppercase tracking-[0.2em] ${isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}`}>
          <span className="inline-flex items-center gap-2">
            <BarChart3 className="h-3.5 w-3.5 text-[color:var(--accent)]" />
            Concentração de receita
          </span>
        </p>
        <div className="mt-1 flex items-end justify-between gap-3">
          <div>
            <p className={`text-sm ${isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}`}>
              Top planos por faturamento em curva de Pareto, com split PF/PJ.
            </p>
          </div>
          <p className={`text-xs tabular-nums ${isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}`}>
            {formatCount(chartData.length)} planos • {currencyCompact(totalFaturamento)}
          </p>
        </div>
      </div>

      <div className="flex-1 min-h-0 p-4">
        {chartData.length ? (
          <div className="h-[520px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} layout="vertical" margin={{ top: 6, right: 44, bottom: 6, left: 12 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={isDark ? 'rgba(148,163,184,0.14)' : 'rgba(148,163,184,0.18)'} />
                <XAxis
                  type="number"
                  xAxisId="revenue"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: isDark ? 'rgba(226,232,240,0.7)' : 'rgba(100,116,139,0.9)', fontSize: 11 }}
                  tickFormatter={(value) => formatarMoeda(Number(value) || 0)}
                />
                <XAxis
                  type="number"
                  xAxisId="share"
                  orientation="top"
                  domain={[0, 100]}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: isDark ? 'rgba(226,232,240,0.7)' : 'rgba(100,116,139,0.9)', fontSize: 11 }}
                  tickFormatter={(value) => formatPercent(value)}
                />
                <YAxis
                  type="category"
                  dataKey="nomeCurto"
                  width={130}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: isDark ? 'rgba(226,232,240,0.85)' : 'rgba(15,23,42,0.82)', fontSize: 11 }}
                />
                <Tooltip content={tooltipContent} cursor={{ fill: isDark ? 'rgba(56,189,248,0.08)' : 'rgba(59,130,246,0.08)' }} />
                <Bar xAxisId="revenue" dataKey="faturamentoPf" stackId="revenue" fill="#38bdf8" radius={[0, 0, 0, 0]} />
                <Bar xAxisId="revenue" dataKey="faturamentoPj" stackId="revenue" fill="#a78bfa" radius={[0, 10, 10, 0]} />
                <Line
                  xAxisId="share"
                  dataKey="acumuladoReceita"
                  stroke="#f59e0b"
                  strokeWidth={2.5}
                  dot={{ r: 4, strokeWidth: 2, fill: isDark ? '#0f172a' : '#ffffff' }}
                  activeDot={{ r: 6 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className={`flex h-[520px] items-center justify-center rounded-[20px] border border-dashed ${
            isDark
              ? 'border-[color:var(--border)] text-[color:var(--text-muted)]'
              : 'border-slate-200 text-slate-500'
          }`}>
            Nenhum dado de plano disponível para o gráfico
          </div>
        )}

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          {chartData.slice(0, 3).map((item) => (
            <div
              key={item.nomeCompleto}
              className={`rounded-2xl border px-3 py-3 ${
                isDark
                  ? 'border-[color:var(--border)] bg-[color:var(--surface-muted)]/55'
                  : 'border-slate-200 bg-slate-50'
              }`}
            >
              <p className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}`}>
                {item.nomeCurto}
              </p>
              <p className={`mt-1 text-sm font-semibold ${isDark ? 'text-[color:var(--text-primary)]' : 'text-slate-900'}`}>
                {currencyCompact(item.faturamentoTotal)}
              </p>
              <p className={`mt-1 text-xs ${isDark ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}`}>
                {formatPercent(item.shareReceita)} da receita
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const FaturamentoTipoPessoaDashboard = ({ resumo, loading = false, cidadeSelecionada = 'todas' }) => {
  const resumoGeral = useMemo(() => resumo || {}, [resumo]);
  const currentTheme = getCurrentTheme();
  const [termoBuscaPlano, setTermoBuscaPlano] = useState('');
  const [planosViewMode, setPlanosViewMode] = useState(getInitialPlanViewMode);
  const [isPlanosModalOpen, setIsPlanosModalOpen] = useState(false);
  const [exportandoXlsx, setExportandoXlsx] = useState(false);

  const cidades = useMemo(() => {
    const lista = Array.isArray(resumoGeral.cidades) ? resumoGeral.cidades : [];
    return lista.map(normalizeCidade).sort((a, b) => Number(b.valorTotal || 0) - Number(a.valorTotal || 0));
  }, [resumoGeral.cidades]);

  const dados = useMemo(() => {
    if (cidadeSelecionada === 'todas') {
      return resumoGeral;
    }

    return cidades.find((cidade) => cidade.cidadeNome === cidadeSelecionada) || resumoGeral;
  }, [cidadeSelecionada, cidades, resumoGeral]);

  const faturamentoCpf = parseNumericValue(dados.faturamentoCpf ?? dados.faturamentoPessoaFisica ?? dados.faturamento_pessoa_fisica ?? dados.faturamento_pessoaFisica);
  const faturamentoCnpj = parseNumericValue(dados.faturamentoCnpj ?? dados.faturamentoPessoaJuridica ?? dados.faturamento_pessoa_juridica ?? dados.faturamento_pessoaJuridica);
  const totalFaturamento = parseNumericValue(dados.valorTotal ?? dados.valor_total) || faturamentoCpf + faturamentoCnpj || 0;
  const totalContratos = parseNumericValue(dados.totalContratos ?? dados.total_contratos);
  const pessoasFisicas = parseNumericValue(dados.pessoasFisicas ?? dados.pessoas_fisicas);
  const pessoasJuridicas = parseNumericValue(dados.pessoasJuridicas ?? dados.pessoas_juridicas);
  const contratosPf = parseNumericValue(dados.contratosPf ?? dados.contratos_pf ?? pessoasFisicas);
  const contratosPj = parseNumericValue(dados.contratosPj ?? dados.contratos_pj ?? pessoasJuridicas);
  const contratosBloqueioManual = parseNumericValue(dados.contratosBloqueioManual ?? dados.contratos_bloqueio_manual);
  const valorBloqueioManual = parseNumericValue(dados.valorBloqueioManual ?? dados.valor_bloqueio_manual);
  const titulosEmitidos = parseNumericValue(dados.titulosEmitidos ?? dados.titulos_emitidos ?? dados.contratosEmitidos);
  const contratosEmitidos = parseNumericValue(dados.contratosEmitidos ?? dados.titulosEmitidos ?? dados.titulos_emitidos);
  const valorEmitido = parseNumericValue(dados.valorEmitido ?? dados.valor_emitido);
  const valorAteVencimento = parseNumericValue(dados.valorAteVencimento ?? dados.valor_ate_vencimento);
  const descontoTotal = parseNumericValue(dados.descontoTotal ?? dados.desconto_total);
  const titulosRecebidos = parseNumericValue(dados.titulosRecebidos ?? dados.titulos_recebidos ?? dados.contratosRecebidos);
  const contratosRecebidos = parseNumericValue(dados.contratosRecebidos ?? dados.titulosRecebidos ?? dados.titulos_recebidos);
  const valorRecebido = parseNumericValue(dados.valorRecebido ?? dados.valor_recebido);
  const titulosEmitidosPf = parseNumericValue(dados.titulosEmitidosPf ?? dados.titulos_emitidos_pf ?? dados.contratosEmitidosPf);
  const contratosEmitidosPf = parseNumericValue(dados.contratosEmitidosPf ?? dados.titulosEmitidosPf ?? dados.titulos_emitidos_pf);
  const valorEmitidoPf = parseNumericValue(dados.valorEmitidoPf ?? dados.valor_emitido_pf);
  const titulosRecebidosPf = parseNumericValue(dados.titulosRecebidosPf ?? dados.titulos_recebidos_pf ?? dados.contratosRecebidosPf);
  const contratosRecebidosPf = parseNumericValue(dados.contratosRecebidosPf ?? dados.titulosRecebidosPf ?? dados.titulos_recebidos_pf);
  const valorRecebidoPf = parseNumericValue(dados.valorRecebidoPf ?? dados.valor_recebido_pf);
  const titulosEmitidosPj = parseNumericValue(dados.titulosEmitidosPj ?? dados.titulos_emitidos_pj ?? dados.contratosEmitidosPj);
  const contratosEmitidosPj = parseNumericValue(dados.contratosEmitidosPj ?? dados.titulosEmitidosPj ?? dados.titulos_emitidos_pj);
  const valorEmitidoPj = parseNumericValue(dados.valorEmitidoPj ?? dados.valor_emitido_pj);
  const titulosRecebidosPj = parseNumericValue(dados.titulosRecebidosPj ?? dados.titulos_recebidos_pj ?? dados.contratosRecebidosPj);
  const contratosRecebidosPj = parseNumericValue(dados.contratosRecebidosPj ?? dados.titulosRecebidosPj ?? dados.titulos_recebidos_pj);
  const valorRecebidoPj = parseNumericValue(dados.valorRecebidoPj ?? dados.valor_recebido_pj);

  useEffect(() => {
    try {
      localStorage.setItem(PLAN_VIEW_STORAGE_KEY, planosViewMode);
    } catch (error) {
      // ignore persistence failures
    }
  }, [planosViewMode]);

  const planos = useMemo(() => {
    const lista = Array.isArray(dados.planos) ? dados.planos : [];
    const termoNormalizado = termoBuscaPlano.trim().toLowerCase();

    return [...lista]
      .map(normalizePlano)
      .filter((plano) => {
        if (!termoNormalizado) {
          return true;
        }

        const nomePlano = String(plano.nome || '').toLowerCase();
        const idPlano = String(plano.idPlano || '').toLowerCase();
        return nomePlano.includes(termoNormalizado) || idPlano.includes(termoNormalizado);
      })
      .sort((a, b) => Number(b.faturamentoTotal || 0) - Number(a.faturamentoTotal || 0));
  }, [dados.planos, termoBuscaPlano]);

  const exportarPlanosXlsx = () => {
    if (!planos.length || exportandoXlsx) {
      return;
    }

    setExportandoXlsx(true);

    try {
      const rows = [
        [
          'Posição',
          'Plano',
          'ID',
          'Contratos totais',
          'Contratos PF',
          'Contratos PJ',
          'Receita total',
          'Participação da receita',
          'Ativos',
          'Suspensos',
          'Bloqueio manual',
          'Títulos emitidos',
          'Valor emitido',
          'Até vencimento',
          'Desconto total',
          'Títulos recebidos',
          'Valor recebido',
          'Emitidos PF',
          'Valor emitido PF',
          'Recebidos PF',
          'Valor recebido PF',
          'Emitidos PJ',
          'Valor emitido PJ',
          'Recebidos PJ',
          'Valor recebido PJ'
        ],
        ...planos.map((plano, index) => {
          const shareReceita = totalFaturamento > 0 ? (Number(plano.faturamentoTotal || 0) / totalFaturamento) * 100 : 0;

          return [
            index + 1,
            plano.nome || 'Plano não informado',
            plano.idPlano ?? '—',
            formatCount(plano.totalContratos),
            formatCount(plano.contratosPf),
            formatCount(plano.contratosPj),
            currencyCompact(plano.faturamentoTotal),
            formatPercent(shareReceita),
            formatCount(plano.ativos),
            formatCount(plano.suspensos),
            formatCount(plano.contratosBloqueioManual),
            formatCount(plano.titulosEmitidos),
            currencyCompact(plano.valorEmitido),
            currencyCompact(plano.valorAteVencimento),
            currencyCompact(plano.descontoTotal),
            formatCount(plano.titulosRecebidos),
            currencyCompact(plano.valorRecebido),
            formatCount(plano.titulosEmitidosPf),
            currencyCompact(plano.valorEmitidoPf),
            formatCount(plano.titulosRecebidosPf),
            currencyCompact(plano.valorRecebidoPf),
            formatCount(plano.titulosEmitidosPj),
            currencyCompact(plano.valorEmitidoPj),
            formatCount(plano.titulosRecebidosPj),
            currencyCompact(plano.valorRecebidoPj)
          ];
        })
      ];

      const resumoRows = [
        ['Indicador', 'Valor', 'Detalhe'],
        ['Planos filtrados', formatCount(planos.length), termoBuscaPlano ? `Busca aplicada: ${termoBuscaPlano}` : 'Sem busca aplicada'],
        ['Receita consolidada', currencyCompact(totalFaturamento), 'Soma do faturamento total dos planos exibidos'],
        ['Contratos totais', formatCount(totalContratos), 'Base consolidada exibida na tela'],
        ['Ticket médio', currencyCompact(ticketMedio), 'Receita média por contrato no recorte ativo'],
        ['Modo de visualização', planosViewMode === 'lista' ? 'Listagem' : 'Tabela', 'Estado atual da interface'],
        ['Cidade', cidadeSelecionada === 'todas' ? 'Todas as cidades' : cidadeSelecionada, 'Recorte ativo na tela']
      ];

      const cidadesExportadas = (cidadeSelecionada === 'todas'
        ? cidades
        : cidades.filter((cidade) => cidade.cidadeNome === cidadeSelecionada)
      ).slice().sort((a, b) => Number(b.valorTotal || 0) - Number(a.valorTotal || 0));

      const receitaTotalCidades = cidadesExportadas.reduce(
        (accumulator, cidade) => accumulator + Number(cidade.valorTotal || 0),
        0
      );

      const resumoCidadesRows = [
        [
          'Posição',
          'Cidade',
          'ID cidade',
          'Contratos totais',
          'Contratos PF',
          'Contratos PJ',
          'Receita total',
          'Ticket médio',
          'Participação da receita',
          'Ativos',
          'Suspensos',
          'Bloqueio manual',
          'Valor bloqueio manual',
          'Títulos emitidos',
          'Valor emitido',
          'Até vencimento',
          'Desconto total',
          'Títulos recebidos',
          'Valor recebido'
        ],
        ...cidadesExportadas.map((cidade, index) => {
          const shareReceita = receitaTotalCidades > 0 ? (Number(cidade.valorTotal || 0) / receitaTotalCidades) * 100 : 0;
          const ticketMedioCidade = Number(cidade.totalContratos || 0) > 0
            ? Number(cidade.valorTotal || 0) / Number(cidade.totalContratos || 0)
            : 0;

          return [
            index + 1,
            cidade.cidadeNome || 'Cidade não informada',
            cidade.idCidade ?? '—',
            formatCount(cidade.totalContratos),
            formatCount(cidade.pessoasFisicas),
            formatCount(cidade.pessoasJuridicas),
            currencyCompact(cidade.valorTotal),
            currencyCompact(ticketMedioCidade),
            formatPercent(shareReceita),
            formatCount(cidade.ativos),
            formatCount(cidade.suspensos),
            formatCount(cidade.contratosBloqueioManual),
            currencyCompact(cidade.valorBloqueioManual),
            formatCount(cidade.titulosEmitidos),
            currencyCompact(cidade.valorEmitido),
            currencyCompact(cidade.valorAteVencimento),
            currencyCompact(cidade.descontoTotal),
            formatCount(cidade.titulosRecebidos),
            currencyCompact(cidade.valorRecebido)
          ];
        })
      ];

      const now = new Date();
      const isoDate = now.toISOString().slice(0, 10);
      const filename = `planos_vendidos_${isoDate}.xlsx`;

      downloadXlsxFile({
        filename,
        sheets: [
          {
            name: 'Planos vendidos',
            title: 'Relatório de Planos Vendidos',
            subtitle: termoBuscaPlano
              ? `Planos filtrados por "${termoBuscaPlano}" - ${formatCount(planos.length)} itens`
              : `${formatCount(planos.length)} planos ${cidadeSelecionada === 'todas' ? 'no total' : `em ${cidadeSelecionada}`}`,
            rows
          },
          {
            name: 'Resumo',
            title: 'Resumo Executivo',
            subtitle: `Exportado em ${new Date().toLocaleString('pt-BR')}`,
            rows: resumoRows
          },
          {
            name: 'Resumo por cidade',
            title: 'Resumo por Cidade',
            subtitle: cidadeSelecionada === 'todas'
              ? `${formatCount(cidadesExportadas.length)} cidades no recorte atual`
              : `Cidade selecionada: ${cidadeSelecionada}`,
            rows: resumoCidadesRows
          }
        ]
      });
    } finally {
      setExportandoXlsx(false);
    }
  };

  const shareCpf = totalFaturamento > 0 ? (faturamentoCpf / totalFaturamento) * 100 : 0;
  const shareCnpj = totalFaturamento > 0 ? (faturamentoCnpj / totalFaturamento) * 100 : 0;
  const ticketMedio = totalContratos > 0 ? totalFaturamento / totalContratos : 0;
  const cards = [
    {
      titulo: cidadeSelecionada === 'todas' ? 'Total de contratos' : 'Contratos da cidade',
      valor: totalContratos.toLocaleString('pt-BR'),
      subtitulo: cidadeSelecionada === 'todas' ? 'Base consolidada da carteira' : `Recorte de ${cidadeSelecionada}`,
      icone: Users,
      corIcone: 'blue',
      featured: true,
      valorCompleto: totalContratos
    },
    {
      titulo: cidadeSelecionada === 'todas' ? 'Valor total' : 'Receita da cidade',
      valor: currencyCompact(totalFaturamento),
      subtitulo: cidadeSelecionada === 'todas' ? 'Receita consolidada' : 'Receita no recorte atual',
      icone: Wallet,
      corIcone: 'green',
      featured: true,
      valorCompleto: totalFaturamento
    },
    {
      titulo: 'Ticket médio',
      valor: currencyCompact(ticketMedio),
      subtitulo: cidadeSelecionada === 'todas' ? 'Receita média por contrato' : `Média por contrato em ${cidadeSelecionada}`,
      icone: TrendingUp,
      corIcone: 'green',
      featured: false,
      valorCompleto: ticketMedio
    },
    {
      titulo: 'Faturamento CPF',
      valor: currencyCompact(faturamentoCpf),
      subtitulo: `${formatCount(contratosPf)} contratos físicos • ${formatPercent(shareCpf)} do total`,
      icone: CreditCard,
      corIcone: 'blue',
      featured: false,
      valorCompleto: faturamentoCpf
    },
    {
      titulo: 'Faturamento CNPJ',
      valor: currencyCompact(faturamentoCnpj),
      subtitulo: `${formatCount(contratosPj)} contratos jurídicos • ${formatPercent(shareCnpj)} do total`,
      icone: Building2,
      corIcone: 'purple',
      featured: false,
      valorCompleto: faturamentoCnpj
    }
  ];

  return (
    <div className={`space-y-6 ${loading ? 'opacity-95' : ''}`}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        {cards.map((card) => (
          <ResumoCard
            key={card.titulo}
            titulo={card.titulo}
            valor={card.valor}
            subtitulo={card.subtitulo}
            icone={card.icone}
            corIcone={card.corIcone}
            valorCompleto={card.valorCompleto}
            featured={card.featured}
          />
        ))}
      </div>

      <section className={`rounded-[28px] border p-5 sm:p-6 ${
        currentTheme === 'dark'
          ? 'border-[color:var(--border)] bg-[color:var(--surface)]/95 shadow-[0_18px_60px_rgba(2,6,23,0.16)]'
          : 'border-slate-200/90 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(248,250,252,0.96))] shadow-[0_20px_52px_rgba(15,23,42,0.09)]'
      }`}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-3xl">
            <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] ${
              currentTheme === 'dark'
                ? 'border-[color:var(--border)] bg-[color:var(--surface-muted)]/60 text-[color:var(--text-muted)]'
                : 'border-slate-200 bg-slate-50 text-slate-500'
            }`}>
              <TrendingUp className="h-3.5 w-3.5 text-[color:var(--accent)]" />
              Resumo operacional
            </div>
            <h2 className="mt-3 text-xl font-semibold text-[color:var(--text-primary)] sm:text-2xl">
              Faturamento e caixa
            </h2>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            label="Bloqueio manual"
            value={formatCount(contratosBloqueioManual)}
            detail={`${currencyCompact(valorBloqueioManual)} em bloqueio`}
            tone="rose"
            currentTheme={currentTheme}
          />
          <MetricCard
            label="Títulos emitidos"
            value={formatCount(titulosEmitidos)}
            detail={`${currencyCompact(valorEmitido)} em títulos emitidos • ${formatCount(contratosEmitidos)} contratos`}
            tone="cyan"
            currentTheme={currentTheme}
          />
          <MetricCard
            label="Até vencimento"
            value={currencyCompact(valorAteVencimento)}
            detail={`${currencyCompact(descontoTotal)} de desconto total • ${formatCount(contratosEmitidos)} contratos`}
            tone="amber"
            currentTheme={currentTheme}
          />
          <MetricCard
            label="Títulos recebidos"
            value={formatCount(titulosRecebidos)}
            detail={`${currencyCompact(valorRecebido)} em títulos recebidos • ${formatCount(contratosRecebidos)} contratos`}
            tone="emerald"
            currentTheme={currentTheme}
          />
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            label="Emitidos PF"
            value={formatCount(titulosEmitidosPf)}
            detail={`${currencyCompact(valorEmitidoPf)} em títulos emitidos • ${formatCount(contratosEmitidosPf)} contratos`}
            tone="cyan"
            currentTheme={currentTheme}
          />
          <MetricCard
            label="Emitidos PJ"
            value={formatCount(titulosEmitidosPj)}
            detail={`${currencyCompact(valorEmitidoPj)} em títulos emitidos • ${formatCount(contratosEmitidosPj)} contratos`}
            tone="violet"
            currentTheme={currentTheme}
          />
          <MetricCard
            label="Recebidos PF"
            value={formatCount(titulosRecebidosPf)}
            detail={`${currencyCompact(valorRecebidoPf)} em títulos recebidos • ${formatCount(contratosRecebidosPf)} contratos`}
            tone="emerald"
            currentTheme={currentTheme}
          />
          <MetricCard
            label="Recebidos PJ"
            value={formatCount(titulosRecebidosPj)}
            detail={`${currencyCompact(valorRecebidoPj)} em títulos recebidos • ${formatCount(contratosRecebidosPj)} contratos`}
            tone="amber"
            currentTheme={currentTheme}
          />
        </div>
      </section>

      <section className={`rounded-[28px] border p-5 sm:p-6 ${
        currentTheme === 'dark'
          ? 'border-[color:var(--border)] bg-[color:var(--surface)]/95 shadow-[0_18px_60px_rgba(2,6,23,0.16)]'
          : 'border-slate-200/90 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(248,250,252,0.96))] shadow-[0_20px_52px_rgba(15,23,42,0.09)]'
      }`}>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] ${
              currentTheme === 'dark'
                ? 'border-[color:var(--border)] bg-[color:var(--surface-muted)]/60 text-[color:var(--text-muted)]'
                : 'border-slate-200 bg-slate-50 text-slate-500'
            }`}>
              <TrendingUp className="h-3.5 w-3.5 text-[color:var(--accent)]" />
              Detalhamento principal
            </div>
            <h2 className="mt-3 text-xl font-semibold text-[color:var(--text-primary)] sm:text-2xl">
              Planos vendidos
            </h2>
            <p className={`mt-1 text-sm leading-relaxed ${currentTheme === 'dark' ? 'text-[color:var(--text-muted)]' : 'text-slate-500'}`}>
              Leitura consolidada por plano, com visão compacta, detalhamento expandido e gráfico lateral.
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={exportarPlanosXlsx}
              disabled={!planos.length || exportandoXlsx}
              className={`inline-flex items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-xs font-semibold transition-colors ${
                currentTheme === 'dark'
                  ? 'border-[color:var(--border)] bg-[color:var(--surface-muted)]/45 text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)]/70 disabled:opacity-50'
                  : 'border-slate-200 bg-white text-slate-700 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:bg-slate-50 disabled:opacity-50'
              }`}
            >
              <Download className="h-4 w-4" />
              {exportandoXlsx ? 'Gerando Excel...' : 'Exportar XLSX'}
            </button>

            <div className={`inline-flex items-center rounded-2xl border p-1 ${
              currentTheme === 'dark'
                ? 'border-[color:var(--border)] bg-[color:var(--surface-muted)]/45'
                : 'border-slate-200 bg-slate-50 shadow-[0_1px_2px_rgba(15,23,42,0.04)]'
            }`}>
              <button
                type="button"
                onClick={() => setPlanosViewMode('tabela')}
                aria-pressed={planosViewMode === 'tabela'}
                className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold transition-colors ${
                  planosViewMode === 'tabela'
                    ? currentTheme === 'dark'
                      ? 'bg-[color:var(--surface)] text-[color:var(--text-primary)] shadow-[0_4px_12px_rgba(2,6,23,0.18)]'
                      : 'bg-white text-slate-900 shadow-[0_4px_12px_rgba(15,23,42,0.08)]'
                    : currentTheme === 'dark'
                      ? 'text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'
                      : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                <Table2 className="h-4 w-4" />
                Tabela
              </button>
              <button
                type="button"
                onClick={() => setPlanosViewMode('lista')}
                aria-pressed={planosViewMode === 'lista'}
                className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold transition-colors ${
                  planosViewMode === 'lista'
                    ? currentTheme === 'dark'
                      ? 'bg-[color:var(--surface)] text-[color:var(--text-primary)] shadow-[0_4px_12px_rgba(2,6,23,0.18)]'
                      : 'bg-white text-slate-900 shadow-[0_4px_12px_rgba(15,23,42,0.08)]'
                    : currentTheme === 'dark'
                      ? 'text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'
                      : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                <List className="h-4 w-4" />
                Listagem
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 grid items-stretch gap-5 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
          <div className="min-w-0 h-full flex flex-col">
            <div className="flex flex-1 min-h-0 flex-col">
              {planosViewMode === 'lista' ? (
                <div className="flex h-full min-h-0 flex-1 flex-col space-y-4 overflow-y-auto pr-1 [scrollbar-gutter:stable]">
                  {planos.length > 0 ? (
                    planos.map((plano, index) => (
                      <PlanCard
                        key={`${plano.idPlano ?? 'plano'}-${index}`}
                        plano={plano}
                        index={index}
                        totalFaturamento={totalFaturamento}
                        currentTheme={currentTheme}
                      />
                    ))
                  ) : (
                    <PlanosTableEmptyState
                      currentTheme={currentTheme}
                      title={termoBuscaPlano ? 'Nenhum plano encontrado para essa busca' : 'Nenhum plano detalhado foi retornado pela API'}
                      description={termoBuscaPlano
                        ? 'Tente outro nome ou limpe o campo para ver todos os planos.'
                        : 'Quando o n8n enviar o array `planos`, ele será listado aqui com o faturamento consolidado.'}
                    />
                  )}
                </div>
              ) : (
                <>
                  <div className="hidden xl:flex h-full min-h-0 flex-1 flex-col">
                    <PlanosCompactTable
                      planos={planos}
                      totalFaturamento={totalFaturamento}
                      currentTheme={currentTheme}
                      onExpand={() => setIsPlanosModalOpen(true)}
                      searchValue={termoBuscaPlano}
                      onSearchChange={setTermoBuscaPlano}
                      onClearSearch={() => setTermoBuscaPlano('')}
                    />
                  </div>

                  <div className="xl:hidden flex h-full min-h-0 flex-1 flex-col space-y-4 overflow-y-auto pr-1 [scrollbar-gutter:stable]">
                    {planos.length > 0 ? (
                      planos.map((plano, index) => (
                        <PlanCard
                          key={`${plano.idPlano ?? 'plano'}-${index}`}
                          plano={plano}
                          index={index}
                          totalFaturamento={totalFaturamento}
                          currentTheme={currentTheme}
                        />
                      ))
                    ) : (
                      <PlanosTableEmptyState
                        currentTheme={currentTheme}
                        title={termoBuscaPlano ? 'Nenhum plano encontrado para essa busca' : 'Nenhum plano detalhado foi retornado pela API'}
                        description={termoBuscaPlano
                          ? 'Tente outro nome ou limpe o campo para ver todos os planos.'
                          : 'Quando o n8n enviar o array `planos`, ele será listado aqui com o faturamento consolidado.'}
                      />
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="min-w-0 h-full">
            <PlanosRevenueChart
              planos={planos}
              totalFaturamento={totalFaturamento}
              currentTheme={currentTheme}
            />
          </div>
        </div>
      </section>

      <Modal
        isOpen={isPlanosModalOpen}
        onClose={() => setIsPlanosModalOpen(false)}
        title="Planos vendidos — visão expandida"
        size="full"
      >
        <div className="flex max-h-[88vh] flex-col gap-4 overflow-hidden">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={exportarPlanosXlsx}
              disabled={!planos.length || exportandoXlsx}
              className={`inline-flex items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-xs font-semibold transition-colors ${
                currentTheme === 'dark'
                  ? 'border-[color:var(--border)] bg-[color:var(--surface-muted)]/45 text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)]/70 disabled:opacity-50'
                  : 'border-slate-200 bg-white text-slate-700 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:bg-slate-50 disabled:opacity-50'
              }`}
            >
              <Download className="h-4 w-4" />
              {exportandoXlsx ? 'Gerando Excel...' : 'Exportar XLSX'}
            </button>
          </div>

          <div className="h-[72vh] overflow-hidden rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface)] shadow-[0_12px_32px_rgba(0,0,0,0.18)]">
            <PlanosExpandedTable
              planos={planos}
              totalFaturamento={totalFaturamento}
              currentTheme={currentTheme}
            />
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            <div className="rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface)] px-4 py-3">
              <p className="text-[10px] uppercase tracking-wide text-[color:var(--text-muted)]">Planos filtrados</p>
              <p className="mt-1 text-sm font-semibold text-[color:var(--text-primary)]">{formatCount(planos.length)}</p>
            </div>
            <div className="rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface)] px-4 py-3">
              <p className="text-[10px] uppercase tracking-wide text-[color:var(--text-muted)]">Receita consolidada</p>
              <p className="mt-1 text-sm font-semibold text-[color:var(--text-primary)]">{currencyCompact(totalFaturamento)}</p>
            </div>
            <div className="rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface)] px-4 py-3">
              <p className="text-[10px] uppercase tracking-wide text-[color:var(--text-muted)]">Contrato total</p>
              <p className="mt-1 text-sm font-semibold text-[color:var(--text-primary)]">{formatCount(totalContratos)}</p>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default FaturamentoTipoPessoaDashboard;
